import { Alert, Button, Card, Form, List, Table, Tag } from 'antd';

import apiCompaniesCall from 'apps/futureflex/apiCalls/company/list';

import { useQueryClient } from '@tanstack/react-query';
import { useFetchApiList, usePostApi } from 'hooks/api';
import MetaDecorator from 'apps/home/components/MetaDecorator';
import PageTitle from 'apps/home/components/PageTitle';
import {
  orderSideColors,
  orderSideLabels,
  programOrderStatus,
  programOrderStatusColors,
  programOrderStatusNew,
} from 'apps/investment/constants/programOrder';
import { orderTypeColors, orderTypeLabels } from 'apps/investment/constants/fundOrder';
import SelectListData from 'common/form/SelectListData';
import { formatTableColumns } from 'common/ui/DataTable/config';
import LoadingSpinner from 'common/ui/LoadingSpinner';
import { Link, useSearchParams } from 'react-router-dom';
import { formatDateTime } from 'utils/formatDate';
import { useEffect } from 'react';

const title = 'Continue Bundle Flex Orders';

const continuePreviewColumns = [
  {
    title: 'ID',
    dataIndex: 'code',
    render: (value, record) => (
      <Link to={`/futureflex/program-orders/${record.id}`}>{value}</Link>
    ),
  },
  {
    title: 'Order Side',
    dataIndex: 'order_side',
    render: text => (
      <Tag color={orderSideColors[text]}>{orderSideLabels[text]}</Tag>
    ),
  },
  {
    title: 'Employee',
    dataIndex: 'employee',
    render: value => value?.full_name,
  },
  {
    title: 'Interval',
    dataIndex: 'interval',
    align: 'right',
  },
  {
    title: 'Order Type',
    dataIndex: 'order_type',
    align: 'center',
    render: (text, record) => (
      <Tag color={orderTypeColors[record.order_type]}>
        {orderTypeLabels[record.order_type] || record.order_type}
      </Tag>
    ),
  },
  {
    title: 'Amount',
    dataIndex: 'amount',
    type: 'formatNumber',
  },
  {
    title: 'Product Code',
    dataIndex: 'product_code',
    align: 'center',
  },
  {
    title: 'Order Date',
    dataIndex: 'order_date',
    render: value => formatDateTime(value),
  },
  {
    title: 'Status',
    dataIndex: 'status',
    align: 'center',
    render: status => (
      <Tag color={programOrderStatusColors[status]}>
        {programOrderStatus[status]}
      </Tag>
    ),
  },
];

function parseSearchParams(searchParams) {
  return {
    company_id: searchParams.get('company_id')
      ? Number(searchParams.get('company_id'))
      : undefined,
  };
}

const ContinueForm = ({ apiCall }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialValues = parseSearchParams(searchParams);
  const [form] = Form.useForm();
  const queryClient = useQueryClient();

  const { post: submitItem, isLoading: submiting } = usePostApi(
    apiCall.continue,
    {
      resourceName: 'data',
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: [apiCall.list.queryKey] });
      },
    }
  );

  const formValues = Form.useWatch([], form) || {};
  const company_id = formValues.company_id;

  const {
    data: programOrders = [],
    load,
    loadMore,
    isLoading,
    isLoadingMore,
    paging,
  } = useFetchApiList(apiCall.list, { resourceName: 'data' });

  useEffect(() => {
    const params = {};
    if (company_id) params.company_id = company_id;
    setSearchParams(params, { replace: true });
  }, [company_id]);

  useEffect(() => {
    if (company_id) {
      load({
        company_id,
        status: programOrderStatusNew,
        order_type: 'buy',
      });
    }
  }, [company_id]);

  const onFinish = () => {
    if (!company_id) return;
    if (
      confirm(
        'Are you sure you want to continue bundling the pending flex orders for this company? This will retry all program orders still stuck in "New" status, regardless of month.'
      )
    ) {
      submitItem({ payload: { company_id } });
    }
  };

  const { page, count, page_size } = paging || {};

  return (
    <div className="flex flex-col gap-6">
      <MetaDecorator title={title} />
      <PageTitle title={title} backUrl={`/futureflex/program-orders`} />

      <Alert
        type="info"
        description={
          <List
            size="small"
            dataSource={[
              '"Continue" dùng để tiếp tục quá trình bundle khi lần "Generate"/"Bundle" trước đó bị lỗi giữa chừng (VD: một nhân viên chưa có tài khoản quỹ) khiến cả lô bị rollback.',
              'Chọn Doanh nghiệp, hệ thống sẽ hiển thị TẤT CẢ các PO đang ở trạng thái "Mới" (không giới hạn theo tháng).',
              'Nhấn "Continue" để hệ thống thử đóng gói lại các PO này thành FO. Có thể bấm nhiều lần cho đến khi thành công, không tạo PO trùng lặp.',
            ]}
            renderItem={item => <List.Item>{item}</List.Item>}
          />
        }
      />

      <Card className="shadow-sm">
        <Form
          form={form}
          name={'continue_form'}
          onFinish={onFinish}
          layout="vertical"
          scrollToFirstError
          initialValues={initialValues}
        >
          <div className="flex gap-4 items-end">
            <Form.Item
              name="company_id"
              label="Company"
              className="flex-1 mb-0"
              rules={[{ required: true, message: 'Please select a company' }]}
            >
              <SelectListData
                apiCall={apiCompaniesCall}
                showSearch
                optionFilterProp="children"
                filterOption={(input, option) =>
                  (option?.label ?? '')
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
                placeholder="Search Email / Full company name"
                callback={({ id, email, name }) => ({
                  value: id,
                  label: name + ' / ' + email,
                })}
              />
            </Form.Item>

            <Form.Item className="mb-0">
              <Button type="primary" htmlType="submit" loading={submiting}>
                Continue
              </Button>
            </Form.Item>
          </div>
        </Form>
      </Card>

      {!!company_id && (
        <Card className="shadow-sm" title="Pending Program Orders (status: New)">
          {isLoading ? (
            <LoadingSpinner />
          ) : (
            <Table
              rowKey={e => e?.id}
              size="small"
              dataSource={programOrders}
              loading={isLoadingMore}
              columns={formatTableColumns(continuePreviewColumns)}
              pagination={{
                current: page,
                total: count,
                hideOnSinglePage: true,
                pageSize: page_size,
                showSizeChanger: false,
                onChange: p => {
                  loadMore({
                    company_id,
                    status: programOrderStatusNew,
                    order_type: 'buy',
                    page: p,
                  });
                },
              }}
            />
          )}
        </Card>
      )}
    </div>
  );
};

export default ContinueForm;
